import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import {
  DateRenderer,
  DateTimeRenderer,
  GridConfigMenu,
  PamGrid,
  createNumberFormatter,
  emptyToNull,
  getWriteErrorMessage,
  notifyPermissionError,
  shouldReloadAfterWriteError,
  useGridConfigs,
  useNotification,
  usePermission,
} from '@1823-partners/core';
import {
  CheckboxEditorModule,
  DateEditorModule,
  NumberEditorModule,
  SelectEditorModule,
  TextEditorModule,
} from 'ag-grid-community';

import api, {
  fetchDealTeamEntries,
  fetchDealTeamSources,
  saveDealTeamEntries,
} from '../api';
import {
  DEFAULT_HIDDEN_COLUMNS,
  buildDealDropdownValues,
  dealLabel,
  isBooleanType,
  isColumnEditable,
  isDateTimeType,
  isDateType,
  isNumericType,
  isStructuredType,
  toAppendPayload,
} from '../utils/dealTeamFields';
import DealModal from './DealModal';
import DealTeamHistoryModal from './DealTeamHistoryModal';

const DEAL_TEAM_GRID_MODULES = [
  TextEditorModule,
  NumberEditorModule,
  SelectEditorModule,
  CheckboxEditorModule,
  DateEditorModule,
];

const rowsEqual = (left, right) => {
  if (!left || !right) return false;
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  return Array.from(keys).every((key) => {
    if (Object.is(left[key], right[key])) return true;
    return JSON.stringify(left[key]) === JSON.stringify(right[key]);
  });
};

const buildRowBaseline = (rows, source) => new Map(rows.map((row) => [
  source?.key_column && row[source.key_column] != null
    ? String(row[source.key_column])
    : JSON.stringify(row),
  { ...row },
]));

// Non-deal sources are hidden from the dropdown by name and kind rather than by
// contract shape: the CRE pipeline views are aggregates, and INTAKE_WATERMARK is
// the intake cursor being retired in favor of Beacon agent runs. A deal table
// missing append-only contract columns stays listed so the workspace can still
// explain the DDL it needs instead of silently reporting no data.
const HIDDEN_SOURCES = new Set(['INTAKE_WATERMARK']);

// The row state the backend's status contract uses for a retired record
// (pam/servers/utils/status_constants.ROW_STATUS_DELETED).
const ROW_STATUS_DELETED = 'Deleted';

const selectableSources = (objects) =>
  objects.filter((item) => !item.is_view && !HIDDEN_SOURCES.has(item.name));

const preferredObject = (objects) =>
  objects.find((item) => item.name === 'DEALS') ||
  objects[0];

const DealTeamWorkspace = () => {
  const [objects, setObjects] = useState([]);
  const [selectedName, setSelectedName] = useState('');
  const [metadata, setMetadata] = useState(null);
  const [rowData, setRowData] = useState([]);
  const [dealDropdownValues, setDealDropdownValues] = useState({});
  const [changedKeys, setChangedKeys] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  // Hidden until known: the shell's permission center loads /my-permissions once.
  const canWrite = usePermission('cre.loans:write');
  // Both calls agree only once the answer is known (they differ while unknown).
  const permissionsKnown =
    usePermission('cre.loans:write', { allowWhileUnknown: true }) === canWrite;
  const [error, setError] = useState('');
  const [hasNoSources, setHasNoSources] = useState(false);
  const [historyRow, setHistoryRow] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [deletingKey, setDeletingKey] = useState(null);
  const gridRef = useRef(null);
  const loadRequestIdRef = useRef(0);
  const rowDataRef = useRef([]);
  const editingKeyRef = useRef(null);
  const originalRowsRef = useRef(new Map());
  const modalBaselineRef = useRef(new Map());
  const { showNotification } = useNotification();
  rowDataRef.current = rowData;
  editingKeyRef.current = editingKey;
  const gridConfigs = useGridConfigs(api, {
    app: 'cre',
    gridId: selectedName ? `deal-team-${selectedName.toLowerCase()}` : null,
    gridApiRef: gridRef,
  });
  const gridConfigComponents = useMemo(() => [
    <GridConfigMenu key="grid-config-menu" controller={gridConfigs} />,
  ], [gridConfigs]);
  const handleGridReady = useCallback((params) => {
    gridConfigs.onGridReady(params);
  }, [gridConfigs]);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setHasNoSources(false);
    fetchDealTeamSources()
      .then(({ data }) => {
        if (!active) return;
        const available = selectableSources(data?.sources || []);
        setObjects(available);
        setSelectedName(preferredObject(available)?.name || '');
        setHasNoSources(available.length === 0);
      })
      .catch(() => {
        if (!active) return;
        setHasNoSources(false);
        setError('Failed to load CRE deal-team sources.');
      })
      .finally(() => active && setIsLoading(false));
    return () => { active = false; };
  }, []);

  const loadObject = useCallback(async (name, silent = false, preserveEdits = false, adoptFreshToken = false) => {
    if (!name) return;
    const requestId = ++loadRequestIdRef.current;
    if (!silent) setIsLoading(true);
    if (!silent) {
      setMetadata(null);
      setRowData([]);
      setDealDropdownValues({});
      setChangedKeys(new Set());
      setHistoryRow(null);
      setEditingKey(null);
    }
    setError('');
    try {
      const { data } = await fetchDealTeamEntries(name);
      if (requestId !== loadRequestIdRef.current) return;
      const rows = data.rows || [];
      const freshBaseline = buildRowBaseline(rows, data.source);
      if (preserveEdits) {
        const previousBaseline = originalRowsRef.current;
        const current = rowDataRef.current;
        const rowKeyOf = (r) => (
          data.source?.key_column && r[data.source.key_column] != null
            ? String(r[data.source.key_column])
            : JSON.stringify(r)
        );
        const nextBaseline = adoptFreshToken ? new Map(freshBaseline) : new Map(
          Array.from(freshBaseline.entries()).map(([key, freshRow]) => {
            const oldRow = previousBaseline.get(key);
            return [
              key,
              oldRow ? { ...freshRow, MODIFIED_DATE: oldRow.MODIFIED_DATE } : freshRow,
            ];
          })
        );
        const merged = rows.map((freshRow) => {
          const key = rowKeyOf(freshRow);
          const currentRow = current.find((r) => rowKeyOf(r) === key);
          if (!currentRow) return freshRow;
          const oldBase = previousBaseline.get(key) || {};
          const diff = Object.fromEntries(
            Object.entries(currentRow).filter(([col, val]) =>
              col !== 'MODIFIED_DATE' && col !== 'MODIFIED_BY' && !Object.is(val, oldBase[col]))
          );
          if (Object.keys(diff).length === 0) return freshRow;
          return {
            ...freshRow,
            ...diff,
            MODIFIED_DATE: adoptFreshToken ? freshRow.MODIFIED_DATE : oldBase.MODIFIED_DATE,
          };
        });
        // Preserve any row with pending grid edits that another user deleted remotely
        // so the edit stays visible and savable (with a null token on 409 to
        // re-create the tombstoned key) instead of vanishing while marked dirty.
        current.forEach((currentRow) => {
          const key = rowKeyOf(currentRow);
          if (freshBaseline.has(key)) return;
          const oldBase = previousBaseline.get(key) || currentRow;
          const preservedToken = adoptFreshToken ? null : oldBase.MODIFIED_DATE;
          const diff = Object.fromEntries(
            Object.entries(currentRow).filter(([col, val]) =>
              col !== 'MODIFIED_DATE' && col !== 'MODIFIED_BY' && !Object.is(val, oldBase[col]))
          );
          if (Object.keys(diff).length > 0) {
            nextBaseline.set(key, { ...oldBase, MODIFIED_DATE: preservedToken });
            merged.push({ ...oldBase, ...diff, MODIFIED_DATE: preservedToken });
          }
        });
        const openKey = editingKeyRef.current;
        if (openKey !== null && !freshBaseline.has(openKey) && !nextBaseline.has(openKey)) {
          // Keep the baseline and modal anchor alive with a null token on 409
          // when the deal open in DealModal was deleted remotely so the modal
          // stays open for retry without polluting rowData on Cancel.
          const oldBase = previousBaseline.get(openKey) || modalBaselineRef.current.get(openKey);
          if (oldBase) {
            const preservedToken = adoptFreshToken ? null : oldBase.MODIFIED_DATE;
            nextBaseline.set(openKey, { ...oldBase, MODIFIED_DATE: preservedToken });
            const modalBase = modalBaselineRef.current.get(openKey) || oldBase;
            modalBaselineRef.current.set(openKey, { ...modalBase, MODIFIED_DATE: preservedToken });
          }
        }
        originalRowsRef.current = nextBaseline;
        setMetadata(data.source);
        setRowData(merged);
        setDealDropdownValues(buildDealDropdownValues(rows, data.source));
      } else {
        originalRowsRef.current = freshBaseline;
        modalBaselineRef.current.clear();
        setMetadata(data.source);
        setRowData(rows);
        setDealDropdownValues(buildDealDropdownValues(rows, data.source));
        setChangedKeys(new Set());
      }
      return true;
    } catch (err) {
      if (requestId !== loadRequestIdRef.current) return;
      setError(err?.response?.data?.error || `Failed to load CRE.${name}.`);
      return false;
    } finally {
      if (!silent && requestId === loadRequestIdRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadObject(selectedName);
  }, [selectedName, loadObject]);

  const getRowId = useCallback((row) => {
    const key = metadata?.key_column;
    return key && row[key] != null ? String(row[key]) : JSON.stringify(row);
  }, [metadata]);

  const columnDefs = useMemo(() => (metadata?.columns || []).map((column) => {
    const isStatus = column.name === metadata.status_column;
    const isEditable = isColumnEditable(column, metadata, canWrite);
    const definition = {
      field: column.name,
      headerName: column.name.replaceAll('_', ' '),
      headerTooltip: isStatus
        ? 'Bitemporal row state: Active or Deleted. Set by the row delete action.'
        : undefined,
      initialHide: DEFAULT_HIDDEN_COLUMNS.has(column.name),
      editable: isEditable,
      sortable: true,
      filter: true,
      resizable: true,
    };
    if (isNumericType(column.data_type)) {
      definition.filter = 'agNumberColumnFilter';
      definition.valueFormatter = createNumberFormatter(4);
      if (isEditable) {
        definition.cellEditor = 'agNumberCellEditor';
        definition.cellEditorParams = {
          precision: Number.isInteger(column.numeric_scale) ? column.numeric_scale : 4,
        };
        definition.valueParser = ({ newValue, oldValue }) => {
          if (newValue === null || newValue === undefined || newValue === '') return null;
          const parsedValue = Number(newValue);
          return Number.isFinite(parsedValue) ? parsedValue : oldValue;
        };
      }
    } else if (isDateTimeType(column.data_type)) {
      definition.cellRenderer = DateTimeRenderer;
    } else if (isDateType(column.data_type)) {
      definition.cellRenderer = DateRenderer;
      if (isEditable) {
        definition.cellEditor = 'agDateStringCellEditor';
        definition.valueParser = ({ newValue, oldValue }) => {
          if (newValue === null || newValue === undefined || newValue === '') return null;
          if (!/^\d{4}-\d{2}-\d{2}$/.test(newValue)) return oldValue;
          const parsedDate = new Date(`${newValue}T00:00:00Z`);
          return !Number.isNaN(parsedDate.getTime()) &&
            parsedDate.toISOString().startsWith(newValue)
            ? newValue
            : oldValue;
        };
      }
    } else if (isBooleanType(column.data_type)) {
      definition.cellDataType = 'boolean';
      definition.cellEditor = 'agCheckboxCellEditor';
    } else if (isStructuredType(column.data_type)) {
      definition.valueFormatter = ({ value }) =>
        value && typeof value === 'object' ? JSON.stringify(value) : value;
    }
    if (dealDropdownValues[column.name]) {
      definition.cellEditor = 'agSelectCellEditor';
      definition.cellEditorParams = {
        values: dealDropdownValues[column.name],
      };
    }
    return definition;
  }), [canWrite, dealDropdownValues, metadata]);

  const handleCellValueChanged = useCallback(({ data, colDef, oldValue, newValue }) => {
    if (oldValue === newValue) return;
    const key = getRowId(data);
    setRowData((current) => current.map((row) => getRowId(row) === key ? { ...data } : row));
    setChangedKeys((current) => {
      const next = new Set(current);
      if (rowsEqual(data, originalRowsRef.current.get(key))) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, [getRowId]);

  const handleViewHistory = useCallback((row) => {
    setHistoryRow(row);
  }, []);

  // The form tracks the deal by key rather than by row object so it always
  // shows the current state of that deal, including unsaved grid edits.
  const handleEditDeal = useCallback((row) => {
    const key = getRowId(row);
    const currentRow = rowData.find((r) => getRowId(r) === key) || row;
    modalBaselineRef.current.set(key, { ...currentRow });
    setEditingKey(key);
  }, [getRowId, rowData]);

  const handleCloseDeal = useCallback(() => {
    if (editingKey !== null) {
      modalBaselineRef.current.delete(editingKey);
      if (!rowData.some((row) => getRowId(row) === editingKey)) {
        originalRowsRef.current.delete(editingKey);
      }
    }
    setEditingKey(null);
  }, [editingKey, getRowId, rowData]);

  const editingRow = useMemo(
    () => (editingKey === null
      ? null
      : rowData.find((row) => getRowId(row) === editingKey) ||
        modalBaselineRef.current.get(editingKey) ||
        null),
    [editingKey, getRowId, rowData],
  );

  // Appending reloads the source, which drops pending grid edits; edits to the
  // deal being formed are saved with it, so only the others have to be settled
  // first.
  const pendingOtherEdits = useMemo(
    () => Array.from(changedKeys).filter((key) => key !== editingKey).length,
    [changedKeys, editingKey],
  );
  const dealBlockedReason = pendingOtherEdits > 0
    ? `Save or revert the ${pendingOtherEdits} pending grid edit(s) first: ` +
      'reloading the source after this save would discard them.'
    : '';

  // Every append path reports a refusal the same way. Permission denials use
  // the shared action-bearing toast so the mounted permission center can open
  // its request dialog; anything else falls back to a per-action message.
  const notifyAppendError = useCallback(async (err, fallback) => {
    if (!notifyPermissionError(showNotification, err)) {
      if (shouldReloadAfterWriteError(err)) {
        await loadObject(selectedName, true, true, err?.response?.status === 409);
      }
      showNotification(getWriteErrorMessage(err, fallback), 'error');
    }
  }, [loadObject, selectedName, showNotification]);

  const handleSaveDeal = useCallback(async (deal) => {
    const savedRow = emptyToNull(deal);
    const savedKey = getRowId(savedRow);
    const baselineRow = originalRowsRef.current.get(savedKey) || savedRow;
    const currentGridRow = rowData.find((row) => getRowId(row) === savedKey) || baselineRow;
    const openedBase = emptyToNull(modalBaselineRef.current.get(savedKey) || currentGridRow);
    // Rebase only the fields the user actually changed in the form onto the
    // current row (which updates on a 409 reload) so retrying after a conflict
    // never overwrites another user's changes to untouched fields, and a
    // cancelled modal never mutates rowData.
    const modalDiff = Object.fromEntries(
      Object.entries(savedRow).filter(([col, val]) =>
        col !== 'MODIFIED_DATE' &&
        col !== 'MODIFIED_BY' &&
        !Object.is(val, openedBase[col]))
    );
    const mergedRow = emptyToNull({ ...currentGridRow, ...modalDiff });
    const payload = toAppendPayload(mergedRow, metadata, baselineRow);
    setIsSaving(true);
    try {
      const response = await saveDealTeamEntries(selectedName, [payload]);
      const dealLabel = metadata?.key_column ? payload[metadata.key_column] : '';
      const modifiedDate = response?.data?.modified_date;
      const adoptedRow = modifiedDate !== undefined
        ? { ...mergedRow, MODIFIED_DATE: modifiedDate }
        : mergedRow;
      modalBaselineRef.current.delete(savedKey);
      // Adopt what was persisted before refreshing, the way the grid save does:
      // if the refresh then fails, the grid still shows the saved deal and no
      // longer counts it as pending, so a later save cannot append a stale row
      // over the version that just landed.
      const existedInGrid = rowData.some((row) => getRowId(row) === savedKey);
      const nextRows = existedInGrid
        ? rowData.map((row) => (getRowId(row) === savedKey ? { ...row, ...adoptedRow } : row))
        : [...rowData, adoptedRow];
      originalRowsRef.current = buildRowBaseline(nextRows, metadata);
      setRowData(nextRows);
      setChangedKeys((current) => {
        const next = new Set(current);
        next.delete(savedKey);
        return next;
      });
      setEditingKey(null);
      const reloaded = await loadObject(selectedName, true);
      if (reloaded) {
        showNotification(`Appended deal update for ${dealLabel}`, 'success');
      } else {
        showNotification(
          `Appended deal update for ${dealLabel}, but failed to refresh the grid`,
          'warning',
        );
      }
    } catch (err) {
      await notifyAppendError(err, 'Failed to save deal updates');
    } finally {
      setIsSaving(false);
    }
  }, [getRowId, loadObject, metadata, notifyAppendError, rowData, selectedName, showNotification]);

  const handleSave = useCallback(async () => {
    const changedRows = rowData
      .filter((row) => changedKeys.has(getRowId(row)))
      .map((row) => {
        const key = getRowId(row);
        const baselineRow = originalRowsRef.current.get(key) || row;
        return toAppendPayload(emptyToNull(row), metadata, baselineRow);
      });
    if (changedRows.length === 0) {
      showNotification('No deal changes to save', 'info');
      return;
    }
    setIsSaving(true);
    try {
      const response = await saveDealTeamEntries(selectedName, changedRows);
      const modifiedDate = response?.data?.modified_date;
      const nextRows = modifiedDate !== undefined
        ? rowData.map((row) => (changedKeys.has(getRowId(row))
          ? { ...row, MODIFIED_DATE: modifiedDate }
          : row))
        : rowData;
      originalRowsRef.current = buildRowBaseline(nextRows, metadata);
      setRowData(nextRows);
      setChangedKeys(new Set());
      const reloaded = await loadObject(selectedName, true);
      if (reloaded) {
        showNotification(`Appended ${changedRows.length} deal update(s)`, 'success');
      } else {
        showNotification(
          `Appended ${changedRows.length} deal update(s), but failed to refresh the grid`,
          'warning',
        );
      }
    } catch (err) {
      await notifyAppendError(err, 'Failed to save deal updates');
    } finally {
      setIsSaving(false);
    }
  }, [
    changedKeys,
    getRowId,
    loadObject,
    metadata,
    notifyAppendError,
    rowData,
    selectedName,
    showNotification,
  ]);

  // Retiring a deal is an append of the same row with the row-status column
  // flipped to Deleted; the read side filters those out, so the deal leaves the
  // grid on the next refresh. STATUS is not editable for exactly this reason —
  // it is the record's state, not a fact about the deal.
  const canDelete = Boolean(metadata?.writable && canWrite && metadata?.status_column);

  const deletingRow = useMemo(
    () => (deletingKey === null
      ? null
      : rowData.find((row) => getRowId(row) === deletingKey) || null),
    [deletingKey, getRowId, rowData],
  );

  const handleRequestDelete = useCallback((row) => {
    // A delete refreshes the source, which would discard pending cell edits.
    if (changedKeys.size > 0) {
      showNotification(
        `Save or revert the ${changedKeys.size} pending grid edit(s) before deleting: ` +
        'reloading the source after a delete would discard them.',
        'warning',
      );
      return;
    }
    setDeletingKey(getRowId(row));
  }, [changedKeys, getRowId, showNotification]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deletingRow) return;
    const deletedKey = getRowId(deletingRow);
    const label = dealLabel(deletingRow, metadata);
    const baselineRow = originalRowsRef.current.get(deletedKey) || deletingRow;
    const payload = toAppendPayload(
      { ...deletingRow, [metadata.status_column]: ROW_STATUS_DELETED },
      metadata,
      baselineRow,
    );
    setIsSaving(true);
    try {
      await saveDealTeamEntries(selectedName, [payload]);
      // Drop the deal locally before refreshing: if the refresh then fails, a
      // deal that is deleted in Snowflake must not still read as Active here.
      const nextRows = rowData.filter((row) => getRowId(row) !== deletedKey);
      originalRowsRef.current = buildRowBaseline(nextRows, metadata);
      setRowData(nextRows);
      setDeletingKey(null);
      const reloaded = await loadObject(selectedName, true);
      if (reloaded) {
        showNotification(`Deleted deal ${label}`, 'success');
      } else {
        showNotification(`Deleted deal ${label}, but failed to refresh the grid`, 'warning');
      }
    } catch (err) {
      await notifyAppendError(err, 'Failed to delete deal');
    } finally {
      setIsSaving(false);
    }
  }, [
    deletingRow,
    getRowId,
    loadObject,
    metadata,
    notifyAppendError,
    rowData,
    selectedName,
    showNotification,
  ]);

  const updatedRows = useMemo(() => Object.fromEntries(
    Array.from(changedKeys, (key) => [key, true])
  ), [changedKeys]);

  const missingContractColumns = Boolean(
    metadata?.is_table && !metadata?.writable && metadata?.missing_write_columns?.length
  );

  return (
    <Box sx={{ p: 2, height: '100%', boxSizing: 'border-box' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 320 }}>
          <InputLabel id="cre-deal-team-source-label">Deal-team source</InputLabel>
          <Select
            labelId="cre-deal-team-source-label"
            label="Deal-team source"
            value={selectedName}
            disabled={isSaving || changedKeys.size > 0}
            onChange={(event) => setSelectedName(event.target.value)}
          >
            {objects.map((item) => (
              <MenuItem key={item.name} value={item.name}>
                {item.name} ({item.is_view ? 'view' : 'table'})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {metadata && (
          <Typography variant="body2" color="text.secondary">
            {metadata.is_view
              ? 'Read-only view'
              : metadata.writable
                ? `Append-only updates · latest row by ${metadata.recency_column}`
                : 'Read-only table'}
          </Typography>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {hasNoSources && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No CRE deal-team data is available yet.
        </Alert>
      )}
      {missingContractColumns && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          CRE.{metadata.name} is read-only because it is missing the append-only contract
          column(s): {(metadata.missing_write_columns || []).join(', ')}.
        </Alert>
      )}
      {metadata?.is_table && metadata?.read_only_reason && (
        <Alert severity="info" sx={{ mb: 2 }}>{metadata.read_only_reason}</Alert>
      )}
      {metadata?.writable && permissionsKnown && !canWrite && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You can view this table, but saving requires the cre.loans:write permission.
        </Alert>
      )}

      <PamGrid
        ref={gridRef}
        modules={DEAL_TEAM_GRID_MODULES}
        title={selectedName ? `CRE.${selectedName}` : 'CRE Deal Team'}
        rowData={rowData}
        columnDefs={columnDefs}
        showToolbar={true}
        customToolbarComponents={gridConfigComponents}
        showQuickFilter={true}
        quickFilterPlaceholder="Filter deals..."
        showSave={Boolean(metadata?.writable && canWrite)}
        onSave={handleSave}
        changedRowCount={changedKeys.size}
        updatedRows={updatedRows}
        isSaving={isSaving}
        isLoading={isLoading || isSaving}
        loadingMessage={isSaving ? 'Appending deal updates...' : 'Loading CRE schema data...'}
        getRowId={getRowId}
        onCellValueChanged={handleCellValueChanged}
        onGridReady={handleGridReady}
        onFilterChanged={gridConfigs.onGridStateChanged}
        showActions={Boolean(
          metadata?.is_table && metadata?.key_column && metadata?.recency_column
        )}
        actions={canDelete ? ['edit', 'history', 'delete'] : ['edit', 'history']}
        onEdit={handleEditDeal}
        onHistory={handleViewHistory}
        onDelete={canDelete ? handleRequestDelete : undefined}
        actionsColumnWidth={canDelete ? 128 : 96}
        agGridProps={{ ...gridConfigs.stashBindings }}
        height="calc(100vh - 225px)"
        rowHeight={32}
        showExport={true}
      />
      <Dialog
        open={Boolean(deletingRow)}
        onClose={() => setDeletingKey(null)}
        aria-label="Delete deal"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete deal</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            {`Mark ${dealLabel(deletingRow, metadata)} as Deleted? `}
            The deal leaves this grid, but the append-only history keeps every
            version of it, so the row action log still shows what it was.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeletingKey(null)} color="secondary">Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isSaving}
          >
            {isSaving ? 'Deleting...' : 'Delete deal'}
          </Button>
        </DialogActions>
      </Dialog>
      {editingRow && (
        <DealModal
          open={true}
          onClose={handleCloseDeal}
          onSave={handleSaveDeal}
          metadata={metadata}
          row={editingRow}
          dropdownValues={dealDropdownValues}
          canWrite={canWrite}
          isSaving={isSaving}
          blockedReason={dealBlockedReason}
        />
      )}
      {historyRow && (
        <DealTeamHistoryModal
          open={true}
          onClose={() => setHistoryRow(null)}
          metadata={metadata}
          row={historyRow}
        />
      )}
    </Box>
  );
};

export default DealTeamWorkspace;
